/**
 * <card-rail> — the scrollbar under a row of cards that becomes a swipe carousel
 * on a phone.
 *
 * The bar is drawn rather than left to the browser: a native scrollbar over a
 * touch carousel is either invisible (iOS hides it until you move) or a heavy
 * grey slab (desktop), and neither says "there is more to the right" while the
 * page is sitting still.
 *
 * It exists only while there is something to scroll. On a desktop grid the row
 * fits, so the bar takes itself off the page rather than sitting there as a
 * full-width line that never moves.
 */
(() => {
  class CardRail extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-rail-viewport]');
      this.bar = this.querySelector('[data-rail-bar]');
      this.thumb = this.querySelector('[data-rail-thumb]');
      this.nav = this.querySelector('[data-rail-nav]');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      if (!this.viewport) return;

      // One card at a time, measured from the row rather than assumed: the card width
      // is a setting, and a fixed step would land between cards at most of its values.
      this.onPrev = () => this.viewport.scrollBy({ left: -this.#step(), behavior: 'smooth' });
      this.onNext = () => this.viewport.scrollBy({ left: this.#step(), behavior: 'smooth' });
      this.prev?.addEventListener('click', this.onPrev);
      this.next?.addEventListener('click', this.onNext);

      this.onScroll = () => this.#paint();
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

      // Catches the breakpoint change from grid to carousel, and any late-loading
      // image that changes how far the row reaches.
      this.observer = new ResizeObserver(() => this.#paint());
      this.observer.observe(this.viewport);

      this.#paint();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.prev?.removeEventListener('click', this.onPrev);
      this.next?.removeEventListener('click', this.onNext);
    }

    #step() {
      const card = this.viewport.firstElementChild?.querySelector('*') || this.viewport.firstElementChild;
      const width = card?.getBoundingClientRect().width || this.viewport.clientWidth * 0.8;
      const gap = parseFloat(getComputedStyle(this.viewport).columnGap) || 0;
      return width + gap;
    }

    #paint() {
      const { scrollLeft, scrollWidth, clientWidth } = this.viewport;
      // A pixel of slack: sub-pixel layout leaves a fraction over at the end, and
      // a bar for one stray pixel is noise.
      const scrollable = scrollWidth - clientWidth > 1;

      if (this.nav) this.nav.hidden = !scrollable;
      if (this.prev) this.prev.disabled = !scrollable || scrollLeft <= 1;
      if (this.next) this.next.disabled = !scrollable || scrollLeft >= scrollWidth - clientWidth - 1;

      if (!this.bar || !this.thumb) return;
      this.bar.hidden = !scrollable;
      if (!scrollable) return;

      // Measured after un-hiding it: a hidden element has no width to share out.
      const track = this.bar.clientWidth;
      // A floor on the thumb: past a dozen cards the proportional size becomes a
      // sliver that reads as a speck of dirt rather than a control.
      const thumb = Math.max(track * (clientWidth / scrollWidth), 24);
      const progress = scrollLeft / (scrollWidth - clientWidth);

      this.style.setProperty('--rail-thumb', `${thumb}px`);
      this.style.setProperty('--rail-offset', `${(track - thumb) * progress}px`);
    }
  }

  if (!customElements.get('card-rail')) {
    customElements.define('card-rail', CardRail);
  }
})();
